import type { getApiReactHooks } from '@frontend/api-client';
import { isLastInitialQuestion } from '@frontend/api-client';
import type {
  ExtractTaskInitialQuestionInput,
  InitialQuestionType,
  TaskInitialQuestionResponse,
} from '@frontend/api-types';
import type { StrictExtract, getMutationHandler } from '@frontend/duck-tape';
import { match } from '@frontend/duck-tape';
import { useCallback, useEffect, useState } from '@frontend/react';
import { Data, Equal } from 'effect';
import { useCreateDraftTask } from '../context/CreateDraftTaskContext';
import type { DraftTaskStepChangeType, InitialQuestionWithAnswerV2, MultiSelectAnswer } from '../types';

type UseInitialQuestionsStepProps = {
  changeStep: (type: DraftTaskStepChangeType) => void;
  handleMutation: ReturnType<typeof getMutationHandler>;
  useSubmitDraftTaskIQAnswersMutation: ReturnType<typeof getApiReactHooks>['useSubmitDraftTaskIQAnswersMutation'];
};

export const useInitialQuestionsStep = ({
  changeStep,
  handleMutation,
  useSubmitDraftTaskIQAnswersMutation,
}: UseInitialQuestionsStepProps) => {
  const { setState, state } = useCreateDraftTask();
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentIQWithAnswer = state.initialQuestionsWithAnswers[currentIndex];
  const isOnLastIQ = currentIQWithAnswer ? isLastInitialQuestion(currentIQWithAnswer) : false;
  const [submitAnswersMutation, { isLoading: isSubmittingAnswers }] = useSubmitDraftTaskIQAnswersMutation();
  const [isUndergoingFinalReview, setIsUndergoingFinalReview] = useState(false);

  const isNextButtonLoading = isSubmittingAnswers || isUndergoingFinalReview;

  useEffect(() => {
    if (isUndergoingFinalReview) {
      if (state.draftTaskEntity?.reviewStatus === 'ready_for_scheduling') {
        setIsUndergoingFinalReview(false);
        changeStep('increment');
      } else if (state.draftTaskEntity?.reviewStatus === 'final_review_failed') {
        setIsUndergoingFinalReview(false);
        setState({
          scopeFailureReason: state.draftTaskEntity.scopeFailureReason ?? undefined,
        });
      }
    }
  }, [state.draftTaskEntity?.reviewStatus]);

  const makeIQStructFromIQAnswer = (iq: InitialQuestionWithAnswerV2) => {
    return Data.struct(
      match(iq)
        .with({ type: 'multi_choice' }, ({ answer, question, questionId, type }) => {
          return {
            answer: Data.array(answer.map((a) => (a.id === 'Other' && !a.value ? 'Other' : a.value)) as string[]),
            question,
            question_id: questionId,
            type,
          } satisfies ExtractTaskInitialQuestionInput<'multi_choice'>;
        })
        .with(
          { type: 'text' },
          (iq) => ({ ...iq, question_id: iq.questionId }) satisfies ExtractTaskInitialQuestionInput<'text'>,
        )
        .with(
          { type: 'secure_note' },
          (iq) =>
            ({
              ...iq,
              question_id: iq.questionId,
              secure_note_id: iq.secureNoteId!,
              secure_note_title: iq.secureNoteTitle,
            }) satisfies ExtractTaskInitialQuestionInput<'secure_note'>,
        )
        .exhaustive(),
    );
  };

  const makeIQStruct = (
    iq: Exclude<TaskInitialQuestionResponse, { type: StrictExtract<InitialQuestionType, 'secure_note'> }>,
  ) =>
    Data.struct(
      match(iq)
        .with({ type: 'multi_choice' }, ({ answer, question, questionId, type }) => {
          return {
            answer: Data.array(answer),
            question,
            question_id: questionId,
            type,
          } satisfies ExtractTaskInitialQuestionInput<'multi_choice'>;
        })
        .with(
          { type: 'text' },
          (iq) => ({ ...iq, question_id: iq.questionId }) satisfies ExtractTaskInitialQuestionInput<'text'>,
        )
        .exhaustive(),
    );

  const findIQ = (id: string) =>
    state.draftTaskEntity?.aiQuestionnaire?.responses.find(
      (response) => response.questionId === id && response.type !== 'secure_note',
    ) as Exclude<TaskInitialQuestionResponse, { type: StrictExtract<InitialQuestionType, 'secure_note'> }>;

  const onSubmitAnswers = async ({
    currentIQWithAnswer,
    successCallback,
  }: {
    currentIQWithAnswer: InitialQuestionWithAnswerV2;
    successCallback?: () => void;
  }) => {
    if (!currentIQWithAnswer || !state.draftTaskEntity) return;

    // Don't re-submit the final question if the draft is already in or past final review
    if (isOnLastIQ && state.draftTaskEntity.reviewStatus === 'final_review') {
      setIsUndergoingFinalReview(true);
      return;
    }

    const updatedIQ = makeIQStructFromIQAnswer(currentIQWithAnswer);
    // questionId is an optional field for backwards compatibility purposes, but in this part of the app it'll always be present
    const foundIQ = findIQ(currentIQWithAnswer.questionId!);
    const originalIQ = foundIQ ? makeIQStruct(foundIQ) : undefined;
    const didUpdateIQ = !Equal.equals(originalIQ, updatedIQ);

    // We always need to submit the last IQ, even if it hasn't changed
    if (!isOnLastIQ && !didUpdateIQ) return;

    return handleMutation({
      mutation: async () =>
        submitAnswersMutation({
          aiQuestionnaire: updatedIQ,
          draftTaskId: state.draftTaskEntity!.id,
          isFinalQuestion: isOnLastIQ,
        }).unwrap(),
      onSuccess: (data) => {
        successCallback?.();
        setState((prev) => ({
          ...prev,
          draftTaskEntity: {
            ...prev.draftTaskEntity!,
            aiQuestionnaire: {
              ...prev.draftTaskEntity?.aiQuestionnaire,
              ownerId: '',
              responses: data.responses,
            },
          },
        }));
      },
      showSuccessToast: false,
    });
  };
  const onSubmitFinalQuestionCallback = () => {
    setIsUndergoingFinalReview(true);
  };

  const changeIQ = useCallback(
    (type: DraftTaskStepChangeType, currentIq = currentIQWithAnswer) =>
      currentIq
        ? match(type)
            .with('increment', async () => {
              if (!isOnLastIQ) {
                await onSubmitAnswers({ currentIQWithAnswer: currentIq });
                setCurrentIndex((prev) => prev + 1);
              } else {
                // Only go to the next step if we're at the last IQ
                await onSubmitAnswers({
                  currentIQWithAnswer: currentIq,
                  successCallback: onSubmitFinalQuestionCallback,
                });
              }
            })
            .with('decrement', () => {
              if (currentIndex === 0) {
                changeStep('decrement');
              } else {
                setCurrentIndex((prev) => prev - 1);
              }
            })
            .exhaustive()
        : undefined,
    [currentIQWithAnswer, isOnLastIQ],
  );

  const isNextButtonDisabled = match(currentIQWithAnswer)
    .with({ type: 'multi_choice' }, (iq) => !iq.answer.length)
    .with({ type: 'text' }, (iq) => (isOnLastIQ ? false : !iq.answer))
    // We can't validate a secure note from this level without hoisting the form state up to the parent component (which we don't want to do)
    .with({ type: 'secure_note' }, () => false)
    .otherwise(() => false);

  const isSameIQ = <TPrev extends InitialQuestionWithAnswerV2, TCurrent extends InitialQuestionWithAnswerV2>(
    prevIQ: TPrev,
    currentIQ: TCurrent,
  ): currentIQ is TCurrent => prevIQ.questionId === currentIQ.questionId;

  const onChangeCheckboxIQ =
    (iq: StrictExtract<InitialQuestionWithAnswerV2, { type: 'multi_choice' }>) => (value: MultiSelectAnswer[]) => {
      setState((prev) => ({
        ...prev,
        initialQuestionsWithAnswers: prev.initialQuestionsWithAnswers.map((prevIQ) =>
          isSameIQ(prevIQ, iq) ? { ...iq, answer: value } : prevIQ,
        ),
      }));
    };

  const onChangeTextIQ = (iq: StrictExtract<InitialQuestionWithAnswerV2, { type: 'text' }>) => (value: string) => {
    setState((prev) => ({
      ...prev,
      initialQuestionsWithAnswers: prev.initialQuestionsWithAnswers.map((prevIQ) =>
        isSameIQ(prevIQ, iq) ? { ...iq, answer: value } : prevIQ,
      ),
    }));
  };

  const onChangeSecureNoteIQ = (iq: StrictExtract<InitialQuestionWithAnswerV2, { type: 'secure_note' }>) => {
    setState((prev) => ({
      ...prev,
      initialQuestionsWithAnswers: prev.initialQuestionsWithAnswers.map((prevIQ) => {
        return isSameIQ(prevIQ, iq) ? iq : prevIQ;
      }),
    }));
  };

  return {
    changeIQ,
    currentIQWithAnswer,
    isNextButtonDisabled,
    isNextButtonLoading,
    isSubmittingAnswers,
    onChangeCheckboxIQ,
    onChangeSecureNoteIQ,
    onChangeTextIQ,
    state,
  };
};
